import React, {FC, useState, useEffect, useMemo} from "react";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage, Typography } from "../../../components";
import { billingService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/display-price";
import { icons } from "../../../icons";
import { useNavigate } from "react-router";
import { useUi } from "../../../context/ui.context";
import { compareArrays } from "../../../utils/findIntersection";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";

const JOURNAL_FILTER_KEYS = [
  'user_id',
  'source',
  'destination',
  'client_credit__client',
  'invoice__client',
  'date_added',
  'destination_amount',
  'source_amount',
];
const JOURNAL_FILTER_TYPES: Record<string, 'date' | 'number' | 'text' | undefined> = {
  user_id: undefined, // Select with search
  source: undefined,
  destination: undefined,
  client_credit__client: undefined, // Select with search for client
  invoice__client: undefined, // Select with search for client
  date_added: 'date',
  destination_amount: 'number',
  source_amount: 'number',
};
const DEFAULT_ORDERING = '-date_added';

const Journal:FC = () => {
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_JOURNAL"));
  const navigate = useNavigate()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [paginationData, setPaginationData] = useState<any>()
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const {
    viewAlert,
  } = useUi()

  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [filterOptions, setFilterOptions] = useState<any>()
  const formatCreditLeft = (invoice:any) => {
    if(invoice.client_credit_left === null || invoice.client_credit_left === undefined || !invoice.client_credit_left_currency){
      return ''
    }

    return formatCurrency(invoice.client_credit_left, invoice.client_credit_left_currency)
  }
  const normalizedFilterOptions = useMemo(() => {
    return JOURNAL_FILTER_KEYS.reduce((acc:any, key) => {
      acc[key] = {
        options: filterOptions?.[key] || [],
        type: JOURNAL_FILTER_TYPES[key]
      }
      return acc;
    }, {});
  }, [filterOptions]);

  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      setAllChecked(false)
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(invoices, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked, invoices])
  
  const getInvoices = () => {
    billingService.getJournal({
      page:page || 1,
      page_size:pageSize || 20,
      ordering: ordering || DEFAULT_ORDERING,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res) => {
      setInvoices(res.data.objects.map((invoice:any) => {
        return {
          ...invoice,
          date_added: timeConverterMMDDYYHHMM(invoice.date_added),
          navigate:invoice.id.toString(),
          source:(
            <div style={{
              display:'flex',
              flexDirection:'column',
              gap:'2px',
            }}>
             <Typography variant="body">
              {invoice?.client?.id ? (
                <span
                  style={{color:'#4466ff', cursor:'pointer'}}
                  onClick={(e) => { e.stopPropagation(); navigate(`/clients-users/clients/${invoice.client.id}`) }}
                >
                  {invoice?.client?.name}
                </span>
              ) : invoice?.client?.name}
             </Typography>
             <Typography variant="body" customStyles={{opacity:'0.5'}}>
              {invoice?.source_info?.name}
             </Typography>
             {invoice?.user && (
               <Typography variant="body" customStyles={{opacity:'0.5'}}>
                {invoice.user.id ? (
                  <span
                    style={{color:'#4466ff', cursor:'pointer'}}
                    onClick={(e) => { e.stopPropagation(); navigate(`/clients-users/users/${invoice.user.id}`) }}
                  >
                    {invoice.user.full_name || invoice.user.email}
                  </span>
                ) : (invoice.user.full_name || invoice.user.email)}
               </Typography>
             )}
            </div>
          ),
          destination:(
            <div style={{
              display:'flex',
              flexDirection:'column',
              gap:'4px',
            }}>
             <Typography variant="body">
              {invoice?.destination_info?.name}
             </Typography>
            </div>
          ),
          destination_amount:formatCurrency(invoice.destination_amount, invoice.destination_currency),
          credit_left:formatCreditLeft(invoice),
        }
      }))
      setPaginationData(res.data)
      setFilterOptions(res.data?.filter_options);
      setAllChecked(false);
      setChecked([]);
      setPageLoading(false)
    })
    .catch((e) => {
      console.log('error', e);
      setPageLoading(false)
    })
  }
  useEffect(() => {
    getInvoices()
    const interval = setInterval(() => {
      getInvoices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString,
    filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const ColumnsInvoices = [
    {
      title:t("DATE"),
      key:'date_added',
      sorted:true,
    },
    {
      title:t("SOURCE_INFO"),
      key:'source',
      sorted:true,
    },
    {
      title:t("DESTINATION_INFO"),
      sorted:true,
      key:'destination'
    },
    {
      title:t("PREPAID_CREDIT_BALANCE"),
      key:'credit_left',
    },
    {
      title:t("AMOUNT"),
      key:'destination_amount',
      sorted:true,
    },
    {
      title:'',
      key:'props'
    }
  ]

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedItem(null)
  }
  const openModal = (modal:any, options?:any) => {
    setModalType(modal)
    setAlertTitle(options?.alertTitle || '')
    setAlertSubTitle(options?.alertSubTitle)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModal = async (values:any) => {
    setLoading(true);
    try {
      if (selectedItem) {
        await billingService.deleteJournalItem(selectedItem.id);
        getInvoices();
        setChecked([]);
        setAllChecked(false);
      } else if (allChecked) {
        await Promise.all(invoices.map((item:any) => billingService.deleteJournalItem(item.id)));
        getInvoices();
        setAllChecked(false);
        setChecked([]);
      } else if (checked?.length > 0) {
        await Promise.all(checked.map((item) => billingService.deleteJournalItem(item.id)));
        getInvoices();
        setChecked([]);
      }
    } catch (e:any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
      setChecked([]);
    } finally {
      setViewModal(false);
      setActiveAnimation(false);
      setSelectedItem(null);
      setLoading(false);
    }
  } 

  const contextMenu = [
    {
      title:t("EDIT"),
      id:'3',
      icon:icons.Edit,
      onClick: (item:any) => {
        navigate(`${item.id}/edit`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:(item:any) => {
        openModal('delete', {
          alertSubTitle:[item],
          alertTitle:t("RECORD"),
          closeModal
        })
        setSelectedItem(item)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  
  return (
    <>
      <StartPage
        title={`${t("JOURNAL")}`}
        noResourceText={`${t("NO_JOURNAL_RECORDS")}`}
        haveResource={invoices.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <Button
                onClick={() => {
                  openModal('delete', {
                    alertSubTitle:allChecked ? invoices : checked,
                    alertTitle:t("JOURNAL"),
                    closeModal
                  })
                  setSelectedItem(null)
                }}
                variant='icon'
                size='display'
                test_id='button-delete'
                disabled={!allChecked && checked?.length === 0 }
              >
                <img src={icons.DeleteIcon}/>
              </Button>
              <Button
                title={t("CLEAR_FILTERS")}
                onClick={handleResetFilters}
                variant='stroke'
                size='display'
                disabled={
                  (!filtering || filtering === '') &&
                  !searchString &&
                  ordering === DEFAULT_ORDERING
                }
              />
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        tableData={{
          columns:ColumnsInvoices,
          data:invoices,
          props:contextMenu,
          paginationData:paginationData,
          updateData:getInvoices,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        instance={selectedItem}
      />
    </>
  )
}

export default Journal
